import React, {useEffect, useState} from 'react';
import {Badge, Box, Button, Divider, Paper} from '@mui/material';
import Typography from '@mui/material/Typography';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import {generateUUID} from '../../../../utility/uuidUtil';
import PropTypes from 'prop-types';
import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd';
import {getComponentByType} from './FormBuilderComponents';
import {ComponentCategory, FORM_BUILDER_COMPONENTS, FormBuilderComponentTypes} from './ComponentList';
import ComponentBuilder from './Components/ComponentBuilder';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import AdmicityDialog from '../../../../shared-components/AdmicityDialog';
import ShowFieldWhen from './ShowFieldWhen';
import VisibilityIcon from '@mui/icons-material/Visibility';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import ConfirmRemoveDialog from './ConfirmRemoveDialog';
import {useGetRelatedFormQuestionAssignmentQuery} from '../../../../api/services/formAssignmentsService';
import Tooltip from '@mui/material/Tooltip';
import ImageWithConditionalRender from '../../../../shared-components/ImageWithConditionalRender';
import ErrorIcon from '@mui/icons-material/Error';
import {validateAge, validateEmail, validatePhoneNumber} from '../../../../utility/validationUtil';
import moment from 'moment/moment';
import Stack from '@mui/material/Stack';
import useNotification from '../../../../utility/hooks/useNotification';

export const availableComponents = [
    FormBuilderComponentTypes.Dropdown,
    FormBuilderComponentTypes.SingleChoice,
    FormBuilderComponentTypes.CheckboxQuestion,
    FormBuilderComponentTypes.MultipleChoice,
    FormBuilderComponentTypes.OfferStatusChoice,
    FormBuilderComponentTypes.BoarderStatusChoice,
    FormBuilderComponentTypes.IntakeChoice,
];

const validators = {
    email: validateEmail,
    tel: validatePhoneNumber,
    text: (value, isRequired) => {
        const isValid = !isRequired || (value != null && value !== '');

        return {
            isValid,
            message: isValid ? '' : 'This field is required'
        };
    },
};

const validateField = (field, submitData, schema, validateOnSubmit = false) => {
    if (field.relates) {
        const related = schema.find(e => e.id === field.relates.relatesTo);
        const relatedValue = submitData?.[related.id];
        const conditionMet = Array.isArray(relatedValue)
            ? relatedValue.includes(field.relates.targetState)
            : relatedValue === field.relates.targetState;

        if (!conditionMet) {
            return {isValid: true, error: ''};
        }
    }

    switch (field.type) {
        case FormBuilderComponentTypes.TickBoxGrid:
        case FormBuilderComponentTypes.MultipleChoiceGrid: {
            if (!field.enableValidation) {
                return {isValid: true, error: ''};
            }

            const data = submitData?.[field.id];
            const gridMissing = !data || field.rows.some(r => !data[r.id] || data[r.id].length === 0);
            return gridMissing
                ? {isValid: false, error: 'Please pick at least one option'}
                : {isValid: true, error: ''};
        }
        case FormBuilderComponentTypes.CheckboxQuestion:
        case FormBuilderComponentTypes.SingleChoice:
        case FormBuilderComponentTypes.OfferStatusChoice:
        case FormBuilderComponentTypes.IntakeChoice:
        case FormBuilderComponentTypes.LookupDropdown:
        case FormBuilderComponentTypes.FileUploader: {
            if (!field.enableValidation) return {isValid: true, error: ''};
            const hasValue = !!submitData?.[field.id];
            return hasValue
                ? {isValid: true, error: ''}
                : {isValid: false, error: 'This field is required'};
        }
        case FormBuilderComponentTypes.MultiLineInput: {
            const inputs = field.inputs || [];
            const data = submitData?.[field.id];

            const fieldErrors = inputs.reduce((errors, input) => {
                if (!input.enableValidation) return errors;

                const value = data?.[input.id];
                if (!value || !value.trim()) {
                    errors[input.id] = 'This field is required';
                }

                return errors;
            }, {});

            const isValid = Object.keys(fieldErrors).length === 0;

            return {
                isValid,
                error: fieldErrors
            };
        }
        case FormBuilderComponentTypes.SingleText: {
            const data = submitData?.[field.id];
            const mustValidate = field.enableValidation || (data !== '' && data !== undefined);

            if (!mustValidate) {
                return {isValid: true, error: ''};
            }

            const validator = validators[field.inputType] ?? ((value) => validators.text(value, field.enableValidation));
            const {isValid, message} = validator(data, field.enableValidation);

            return {isValid, error: message};
        }
        case FormBuilderComponentTypes.Address: {
            if (!validateOnSubmit) {
                return {isValid: true, error: {}};
            }

            const data = submitData?.[field.id];
            const fieldErrors = {};

            ['street', 'town', 'postcode'].forEach(input => {
                const value = data[input];

                if (!value || value.trim() === '') {
                    fieldErrors[input] = 'This field is required';
                }
            });
            const isValid = Object.keys(fieldErrors).length === 0;

            return {
                isValid,
                error: fieldErrors
            };
        }
        case FormBuilderComponentTypes.NumericInputBox: {
            const raw = submitData?.[field.id];
            if (raw === '' || raw === undefined || raw === null) {
                return field.enableValidation
                    ? { isValid: false, error: 'This field is required' }
                    : { isValid: true, error: '' };
            }
            return { isValid: true, error: '' };
        }

        default: {
            const empty = field.enableValidation && (submitData?.[field.id] === undefined || submitData[field.id] === '');
            return empty
                ? {isValid: false, error: 'This field is required'}
                : {isValid: true, error: ''};
        }
    }
};

const validateSchemaField = (field) => {
    switch (field.type) {
        case FormBuilderComponentTypes.Date: {
            if (field.minAge == null) {
                return '';
            }

            return validateAge(Number(field.minAge));
        }
        case FormBuilderComponentTypes.DateTime: {
            const minDate = field.minDateTime;
            const maxDate = field.maxDateTime;

            if (minDate && maxDate) {
                return moment(minDate).isAfter(moment(maxDate))
                    ? 'Min date time cannot be after max date time'
                    : '';
            }

            return '';
        }
        case FormBuilderComponentTypes.OfferStatusChoice: {
            if (field && (!field.options?.length ||
                !field.options?.every(o => o.value !== '' && o.value != null))) {
                return 'Please configure the statuses for Application Status Choice component';
            }

            return '';
        }
        case FormBuilderComponentTypes.LookupDropdown: {
            if (field && (!field.lookupType ||
                field.lookupType === '')) {
                return 'Please select a lookup type for the Lookup Dropdown component';
            }

            return '';
        }
        case FormBuilderComponentTypes.IntakeChoice: {
            if (field && (!field.options?.length ||
                !field.options?.every(o => o.value !== '' && o.value != null))) {
                return 'Please configure the intakes for Intake Choice component';
            }

            if (field && field.applicationStatusId == null) {
                return 'Please Please select an application status to assign on form submission';
            }

            return '';
        }
        case FormBuilderComponentTypes.BoarderStatusChoice: {
            if (field && (!field.options?.length ||
                !field.options?.every(o => o.value !== '' && o.value != null))) {
                return 'Please configure the statuses for Boarder Status Choice component';
            }

            return '';
        }
        case FormBuilderComponentTypes.NumericInputBox: {
            const k = field.fieldKey ?? field.key;
            if (k && k.toLowerCase().includes('priority')) {
                const n = Number(field.priority);
                if (!Number.isInteger(n) || n <= 0 || n > 2) {
                    if (k === 'parent1Priority') {
                        return 'Please set Parent 1 priority to a positive whole number (1 or 2).';
                    }
                    if (k === 'parent2Priority') {
                        return 'Please set Parent 2 priority to a positive whole number (1 or 2).';
                    }
                    return 'Please set priority to a positive whole number (1 or 2).';
                }
            }
            return '';
        }
        default: {
            return '';
        }
    }
};

const validateSchema = (schema) => {
    const errors = {};
    let hasError = false;

    schema.forEach(f => {
        const validationResult = validateSchemaField(f);

        errors[f.id] = validationResult;

        if (!hasError && validationResult) {
            hasError = true;
        }
    });

    return errors;
};

const MAX_FILE_UPLOADER_COMPONENTS_COUNT = 5;

const formComponentsByCategory = FORM_BUILDER_COMPONENTS.reduce((acc, item) => {
    const {category} = item;
    acc[category] = acc[category] || [];
    acc[category].push(item);
    return acc;
}, {});

const FormBuilder = ({
                         schema,
                         schemaId,
                         onSchemaChange,
                         onSchemaValidationChange,
                         submitMode = false,
                         handleSubmit,
                         handleDownloadAttachment,
                         data,
                         readOnly,
                         isFormArchived,
                         placeholderValues,
                         accessLevel,
                         formId,
                         componentToEditIndex,
                         setComponentToEditIndex
                     }) => {
    if (!schema) return <></>;

    const [previewMode, setPreviewMode] = useState(false);
    const [submitData, setSubmitData] = useState({});
    const [currentComponent, setCurrentComponent] = useState(undefined);
    const [relatedComponents, setRelatedComponents] = useState([]);
    const [componentToConfirmRemove, setComponentToConfirmRemove] = useState(undefined);
    const [formPlaceholders, setFormPlaceholders] = useState(undefined);
    const [errors, setErrors] = useState({});
    const [schemaErrors, setSchemaErrors] = useState({});
    const {data: relatedFormQuestionAssignData} = useGetRelatedFormQuestionAssignmentQuery(formId, {skip: submitMode || !formId});
    const {showErrorNotification} = useNotification();

    useEffect(() => {
        setRelatedComponents(schema.filter(e => availableComponents.includes(e.type) && e.id !== currentComponent?.id));
    }, [schema]);

    useEffect(() => {
        if (submitMode) {
            setFormPlaceholders(placeholderValues);
        }
    }, [placeholderValues]);

    useEffect(() => {
        const validation = !readOnly
            ? validateSchema(schema)
            : {};

        if (onSchemaValidationChange) {
            onSchemaValidationChange(validation);
        }

        setSchemaErrors(validation);
    }, [schemaId, schema]);

    const handleSetSchema = (newSchema) => {
        const validation = !readOnly
            ? validateSchema(newSchema)
            : {};

        onSchemaChange(newSchema);

        if (onSchemaValidationChange) {
            onSchemaValidationChange(validation);
        }

        setSchemaErrors(validation);
    };

    const handleChangeEdit = ({
                                  index,
                                  description,
                                  bookingLink,
                                  paymentLink,
                                  priority,
                                  validation,
                                  enableValidation,
                                  option,
                                  optionId,
                                  isAdd,
                                  isRemove,
                                  files,
                                  listKey,
                                  startFrom,
                                  endAt,
                                  startLabel,
                                  endLabel,
                                  formattedDescription,
                                  lookupType,
                                  minAge,
                                  minDateTime,
                                  maxDateTime,
                                  inputType,
                                  applicationStatusId
                              }) => {
        const newForm = [...schema];
        const updatedItem = structuredClone(newForm[index]);

        if (description !== undefined) {
            updatedItem.description = description;
        }
        if (formattedDescription !== undefined) {
            updatedItem.formattedDescription = formattedDescription;
        }
        if (bookingLink !== undefined) {
            updatedItem.bookingLink = bookingLink;
        }
        if (paymentLink !== undefined) {
            updatedItem.paymentLink = paymentLink;
        }
        if (priority === undefined || priority === '') {
            delete updatedItem.priority
        } else {
            updatedItem.priority = priority;
        }
        if (validation !== undefined) {
            updatedItem.validation = validation;
        }
        if (enableValidation !== undefined) {
            updatedItem.enableValidation = enableValidation;
        }
        if (option !== undefined && !listKey) {
            const optIndex = updatedItem.options.findIndex(e => e.id === optionId);
            if (optIndex !== -1) {
                const opt = {...updatedItem.options[optIndex]};
                opt.description = option.description;

                if (Object.prototype.hasOwnProperty.call(option, 'value')) {
                    opt.value = option.value;
                }
                const options = [...updatedItem.options];
                options[optIndex] = opt;
                updatedItem.options = options;
            }
        }
        if (isAdd && !listKey) {
            updatedItem.options = [
                ...updatedItem.options,
                {id: generateUUID()}
            ];
        }
        if (isRemove && !listKey) {
            const relatedComponent = schema.find(e => e.relates && e.relates.targetState === optionId);
            if (relatedComponent) {
                setComponentToConfirmRemove({
                    component: relatedComponent,
                    handleRemove: () => removeOptionModal(index, optionId)
                });
            } else {
                updatedItem.options = updatedItem.options.filter(e => e.id !== optionId);
            }
        }
        if (listKey) {
            if (isRemove) {
                updatedItem[listKey] = updatedItem[listKey].filter(
                    (item, idx) => idx !== optionId && item.id !== optionId
                );
            } else if (isAdd) {
                const newOption =
                    typeof option === 'object' && option !== null
                        ? option
                        : {title: '', id: generateUUID()};

                updatedItem[listKey] = [...updatedItem[listKey], newOption];
            } else {
                const index =
                    typeof optionId === 'number'
                        ? optionId
                        : updatedItem[listKey].findIndex((i) => i.id === optionId);

                if (index !== -1) {
                    const current = updatedItem[listKey][index];

                    updatedItem[listKey][index] =
                        typeof option === 'string'
                            ? {...current, title: option}
                            : {...current, ...option};
                }
            }
        }
        if (files !== undefined) {
            updatedItem.attachments = files;
        }
        if (startFrom !== undefined) {
            updatedItem.startFrom = startFrom;
        }
        if (endAt !== undefined) {
            updatedItem.endAt = endAt;
        }
        if (startLabel !== undefined) {
            updatedItem.startLabel = startLabel;
        }
        if (endLabel !== undefined) {
            updatedItem.endLabel = endLabel;
        }
        if (lookupType !== undefined) {
            updatedItem.lookupType = lookupType;
        }
        if (minAge !== undefined) {
            updatedItem.minAge = minAge;
        }
        if (minDateTime !== undefined) {
            updatedItem.minDateTime = minDateTime;
        }
        if (maxDateTime !== undefined) {
            updatedItem.maxDateTime = maxDateTime;
        }
        if (inputType !== undefined) {
            updatedItem.inputType = inputType;
        }
        if (applicationStatusId !== undefined) {
            updatedItem.applicationStatusId = applicationStatusId;
        }

        newForm[index] = updatedItem;
        handleSetSchema(newForm);
    };

    const handleShowFieldWhen = (id, relatesTo, targetState) => {
        handleDialogClose();
        const newForm = JSON.parse(JSON.stringify(schema));
        let updatedComponent = newForm.find(e => e.id === id);

        if (relatesTo && targetState !== undefined) {
            updatedComponent.relates = {relatesTo, targetState};
        } else {
            delete updatedComponent.relates;
        }

        handleSetSchema(newForm);
    };

    const handleChangeSubmit = ({id, value, optionIndex, optionValue, radioValue}) => {
        if (readOnly) return;

        setSubmitData(prev => {
            let newSubmitData = { ...prev };

            if (value !== undefined) {
                newSubmitData[id] = value;
            } else if (optionIndex !== undefined && optionValue !== undefined) {
                if (!newSubmitData[id]) {
                    newSubmitData[id] = [];
                }

                newSubmitData[id] = optionValue ? [...newSubmitData[id], optionIndex] : newSubmitData[id].filter(o => o !== optionIndex);
                if (newSubmitData[id].length === 0) {
                    newSubmitData[id] = undefined;
                }
            } else if (radioValue !== undefined) {
                if (radioValue === null) {
                    delete newSubmitData[id]
                } else {
                    newSubmitData[id] = radioValue;
                }
            }

            schema?.forEach(field => {
                if (!checkIfVisible(field.relates, newSubmitData)) {
                    delete newSubmitData[field.id];
                }
            });

            const field = schema.find(field => field.id === id);

            if (field) {
                setErrors(prev => ({
                    ...prev,
                    [field.id]: validateField(field, newSubmitData, schema)
                }));
            }

            return newSubmitData;
        });
    };

    const removeComponent = (position) => {
        let componentToRemove = schema.find((e, index) => index === position);
        const relatedComponents = schema.filter(e => e.relates && e.relates.relatesTo === componentToRemove.id);

        if (relatedComponents.length > 0) {
            setComponentToConfirmRemove({
                components: relatedComponents,
                handleRemove: () => removeComponentModal(componentToRemove.id)
            });
        } else {
            handleSetSchema(schema.filter((e, index) => index !== position));
        }
    };

    const removeComponentModal = (id) => {
        const newForm = JSON.parse(JSON.stringify(schema));
        newForm.forEach(e => {
            if (e.relates && e.relates.relatesTo === id) {
                delete e.relates;
            }
        });
        handleSetSchema(newForm.filter((e) => e.id !== id));
        setComponentToConfirmRemove(undefined);
    };

    const removeOptionModal = (index, optionId) => {
        const newForm = JSON.parse(JSON.stringify(schema));
        newForm.forEach(e => {
            if (e.relates && e.relates.targetState === optionId) {
                delete e.relates;
            }
        });
        newForm[index].options = newForm[index].options.filter(e => e.id !== optionId);
        handleSetSchema(newForm);
        setComponentToConfirmRemove(undefined);
    };

    const moveComponent = (position, swapPosition) => {
        let newArray = [...schema];
        let temp = newArray[position];
        newArray[position] = newArray[swapPosition];
        newArray[swapPosition] = temp;
        handleSetSchema(newArray);
    };

    const insertComponent = (sourceIndex, destinationIndex) => {
        const newArray = [...schema];
        const [movedItem] = newArray.splice(sourceIndex, 1);
        newArray.splice(destinationIndex, 0, movedItem);
        handleSetSchema(newArray);
    };

    const onDragEnd = result => {
        setComponentToEditIndex(undefined);
        if (!result.destination) {
            return;
        }

        const sourceIndex = result.source.index;
        const destinationIndex = result.destination.index;

        if (sourceIndex === destinationIndex) {
            return;
        }

        insertComponent(sourceIndex, destinationIndex);
    };

    const addComponent = (type) => {
        const newComponent = {
            ...getComponentByType(type),
            enableValidation: type === 'signature',
            id: generateUUID()
        };

        const filteredSchema = schema.filter(component => component.type !== 'schoolLogo');

        handleSetSchema([
            ...filteredSchema,
            newComponent,
            ...schema.filter(component => component.type === 'schoolLogo')
        ]);
    };

    const handleDialogClose = () => {
        setCurrentComponent(undefined);
    };

    const buildEditRows = (f, index) => {
        const relatedComponent = relatedFormQuestionAssignData?.find(e => e.relatedComponentId === f.id);
        return <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} py={1}>
            <Typography>{index + 1}. {f.label}</Typography>
            <Box display={'flex'} alignItems={'center'}>
                {!isFormArchived &&
                    <IconButton>
                        <EditIcon onClick={() => setComponentToEditIndex(index)}/>
                    </IconButton>
                }
                {!isFormArchived && !f.disableDelete &&
                    <Tooltip
                        title={relatedComponent
                            ? `This question cannot be deleted as it is currently used in the ${relatedComponent?.relatedFormName} form.`
                            : null}>
                        <IconButton>
                            <DeleteIcon
                                onClick={() => relatedComponent ? {} : removeComponent(index)}/>
                        </IconButton>
                    </Tooltip>
                }
                {!isFormArchived && relatedComponents.filter(c => c.id !== f.id).length > 0 && !f.disableHide &&
                    <IconButton>
                        <VisibilityIcon color={f.relates ? 'primary' : ''} onClick={() => setCurrentComponent(f)}/>
                    </IconButton>
                }
                {index !== 0 && !isFormArchived &&
                    <IconButton>
                        <ArrowUpwardOutlinedIcon onClick={() => moveComponent(index, index - 1)}/>
                    </IconButton>
                }
                {index !== schema.length - 2 && !isFormArchived &&
                    <IconButton>
                        <ArrowDownwardOutlinedIcon onClick={() => moveComponent(index, index + 1)}/>
                    </IconButton>
                }
            </Box>
        </Box>;
    };

    const checkIfVisible = (relates, submitData) => {
        if (relates) {
            const relatedComponent = readOnly ? data[relates.relatesTo] : submitData[relates.relatesTo];
            return Array.isArray(relatedComponent)
                ? relatedComponent.includes(relates.targetState)
                : relatedComponent === relates.targetState || (relates.targetState === false && relatedComponent === undefined);
        }
        return true;
    };

    const schoolLogoComponent = schema.find(x => x.type === 'schoolLogo');

    const onSubmit = () => {
        const validation = {};
        let isError = false;

        schema.forEach(f => {
            const validationResult = validateField(f, submitData, schema, true);

            if (!isError && !validationResult.isValid) {
                isError = true;
            }

            validation[f.id] = validationResult;
        });

        if (isError) {
            setErrors(validation);
            showErrorNotification('Please review the form and fill in all required fields')

            return;
        }

        const hiddenFieldIds = schema
            ?.filter(field => !checkIfVisible(field.relates, submitData))
            .map(field => field.id);
        const filteredData = Object.keys(submitData)
            .filter(key => !hiddenFieldIds.includes(key))
            .reduce((acc, key) => {
                acc[key] = submitData[key];
                return acc;
            }, {});

        handleSubmit(filteredData, JSON.stringify(formPlaceholders));
    };

    const renderComponentButton = ({type, description}) => {
        const isUploader = type === FormBuilderComponentTypes.FileUploader;
        const limitExceeded = isUploader && schema.filter(
            (x) => x.type === FormBuilderComponentTypes.FileUploader,
        ).length >= MAX_FILE_UPLOADER_COMPONENTS_COUNT;
        const disabled = isFormArchived || limitExceeded;

        const button = (
            <Button
                key={type}
                variant="outlined"
                disabled={disabled}
                onClick={() => {
                    addComponent(type);
                }}
            >
                {description}
            </Button>
        );

        if (!limitExceeded) return button;

        return (
            <Badge
                key={type}
                sx={{'& .MuiBadge-badge': {top: 5, padding: '0 4px'}}}
                badgeContent={
                    <Tooltip title="Maximum of 5 file upload fields allowed on the form">
                        <ErrorIcon color="warning"/>
                    </Tooltip>
                }
            >
                {button}
            </Badge>
        );
    };

    return (
        submitMode
            ? <Box display="flex" flexDirection="column">
                {
                    schoolLogoComponent?.url
                        ? <Box textAlign="center">
                            <ImageWithConditionalRender imageUrl={schoolLogoComponent?.url} alt="School logo"/>
                        </Box>
                        : ''
                }
                {schema?.filter(e => checkIfVisible(e.relates, submitData))
                    .map((f, index) =>
                        f.type !== 'schoolLogo'
                            ? <Box key={`formItem-${index}`}>
                                <ComponentBuilder handleChangeSubmit={handleChangeSubmit}
                                                  handleDownloadAttachment={handleDownloadAttachment}
                                                  index={index} readOnly={readOnly}
                                                  handleChangeEdit={handleChangeEdit}
                                                  previewMode={componentToEditIndex === index || previewMode}
                                                  formPreviewMode={previewMode}
                                                  field={f}
                                                  componentToEditIndex={componentToEditIndex}
                                                  data={data} submitMode={submitMode}
                                                  submitData={submitData} isFormArchived={isFormArchived}
                                                  placeholderValues={formPlaceholders}
                                                  error={submitMode ? errors[f.id]?.error : schemaErrors[f.id]}
                                />
                            </Box>
                            : '')
                }
                {
                    <Box>
                        {!readOnly && !previewMode && <Divider/>}
                        <Box display="flex" justifyContent="end" marginTop={2}>
                            {
                                readOnly || previewMode
                                    ? null
                                    : <Button
                                        variant="contained"
                                        onClick={onSubmit}>
                                        Submit
                                    </Button>
                            }
                        </Box>
                    </Box>
                }
            </Box>
            : <Box onDoubleClick={() => submitMode
                ? () => {
                }
                : setComponentToEditIndex(undefined)}>
                <Box display={'flex'} justifyContent={'end'}>{!submitMode &&
                    <Tooltip
                        title={componentToEditIndex >= 0 ? 'Preview is disabled while editing components. Double-click outside the component area to exit edit mode and enable the preview.' : null}>
                        <span>
                            <Button disabled={componentToEditIndex >= 0}
                                    onClick={() => componentToEditIndex >= 0 ? setComponentToEditIndex(undefined) : setPreviewMode(!previewMode)}
                                    sx={{width: '100px'}}>
                                {previewMode ? 'Edit' : 'Preview'}
                            </Button>
                        </span>
                    </Tooltip>}
                </Box>
                <Box display={'flex'} fullWidth justifyContent={'center'}>
                    <Box
                        sx={{
                            flex: 2,
                            p: 2,
                            mx: previewMode ? '25%' : '20%',
                            mb: 2
                        }}
                    >
                        {
                            schoolLogoComponent?.url
                                ? <Box textAlign="center">
                                    <ImageWithConditionalRender imageUrl={schoolLogoComponent?.url} alt="School logo"/>
                                </Box>
                                : ''
                        }
                        {!previewMode
                            ? <DragDropContext onDragEnd={onDragEnd}>
                                <Droppable droppableId="droppable">
                                    {(provided) => (
                                        <Box
                                            {...provided.droppableProps}
                                            ref={provided.innerRef}
                                        >
                                            {schema?.map((f, index) =>
                                                f.type !== 'schoolLogo'
                                                    ? <Draggable key={index} draggableId={index.toString()}
                                                                 index={index}
                                                                 isDragDisabled={isFormArchived}>
                                                        {(provided) => (
                                                            <Box
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                {...provided.dragHandleProps}>
                                                                <Paper sx={{
                                                                    p: 2,
                                                                    mb: 2,
                                                                    borderColor: schemaErrors[f.id] ? 'error.main' : ''
                                                                }}
                                                                       variant="outlined"
                                                                       key={`formItem-${index}-${f.description}`}
                                                                >
                                                                    {buildEditRows(f, index)}
                                                                    <ComponentBuilder
                                                                        handleChangeSubmit={handleChangeSubmit}
                                                                        index={index} readOnly={readOnly}
                                                                        handleChangeEdit={handleChangeEdit}
                                                                        previewMode={componentToEditIndex !== index || previewMode}
                                                                        formPreviewMode={previewMode}
                                                                        field={f}
                                                                        componentToEditIndex={componentToEditIndex}
                                                                        data={data} submitMode={submitMode}
                                                                        submitData={submitData}
                                                                        isFormArchived={isFormArchived}
                                                                        accessLevel={accessLevel}
                                                                        relatedComponent={relatedFormQuestionAssignData
                                                                            ?.find(e => e.relatedComponentId === f.id)}
                                                                        error={submitMode ? errors[f.id]?.error : schemaErrors[f.id]}/>
                                                                </Paper>
                                                            </Box>)}
                                                    </Draggable>
                                                    : '')}
                                            {provided.placeholder}
                                        </Box>)}
                                </Droppable>
                            </DragDropContext>
                            : schema?.filter(e => checkIfVisible(e.relates, submitData))
                                .map((f, index) =>
                                    f.type !== 'schoolLogo'
                                        ? <Box key={`formItem-${index}`}>
                                            <ComponentBuilder handleChangeSubmit={handleChangeSubmit}
                                                              handleDownloadAttachment={handleDownloadAttachment}
                                                              index={index} readOnly={readOnly}
                                                              handleChangeEdit={handleChangeEdit}
                                                              previewMode={componentToEditIndex === index || previewMode}
                                                              formPreviewMode={previewMode}
                                                              field={f}
                                                              componentToEditIndex={componentToEditIndex}
                                                              data={data} submitMode={submitMode}
                                                              submitData={submitData} isFormArchived={isFormArchived}
                                                              placeholderValues={formPlaceholders}
                                                              error={submitMode ? errors[f.id]?.error : schemaErrors[f.id]}/>
                                        </Box>
                                        : '')
                        }
                        <Box
                            display={'flex'}
                            alignItems={'center'}
                            justifyContent={submitMode ? 'end' : 'start'}
                            gap={1}
                            pt={1}
                        >
                            {!previewMode
                                ? <>
                                        <Stack spacing={2}>
                                            {Object.values(ComponentCategory).map((category) => {
                                                const items = formComponentsByCategory[category] || [];

                                                if (!items.length) return null;

                                                return (
                                                    <Box key={category}>
                                                        <Typography variant="subtitle2" sx={{mb: 1}} color="textSecondary">
                                                            {category}
                                                        </Typography>
                                                        <Stack direction="row" flexWrap="wrap" gap={1}>
                                                            {items.map(renderComponentButton)}
                                                        </Stack>
                                                    </Box>
                                                );
                                            })}
                                        </Stack>
                                </>
                                : readOnly || previewMode
                                    ? null
                                    : <Button onClick={onSubmit}>Submit</Button>}
                        </Box>
                    </Box>
                    <AdmicityDialog
                        handleClose={handleDialogClose}
                        title={'Show Field When'}
                        actions={[]}
                        open={Boolean(currentComponent)}
                        maxWidth={'md'}
                    >
                        <ShowFieldWhen component={currentComponent} handleShowFieldWhen={handleShowFieldWhen}
                                       availableComponents={relatedComponents.filter(c => c.id !== currentComponent?.id)}/>
                    </AdmicityDialog>
                    <ConfirmRemoveDialog
                        open={Boolean(componentToConfirmRemove)}
                        handleClose={() => setComponentToConfirmRemove(undefined)}
                        handleConfirm={componentToConfirmRemove?.handleRemove}
                        relatedComponents={componentToConfirmRemove?.components}/>
                </Box>
            </Box>);
};

FormBuilder.propTypes = {
    schema: PropTypes.array,
    schemaId: PropTypes.number,
    onSchemaChange: PropTypes.func,
    onSchemaValidationChange: PropTypes.func,
    submitMode: PropTypes.bool,
    handleSubmit: PropTypes.func,
    handleDownloadAttachment: PropTypes.func,
    data: PropTypes.object,
    readOnly: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    accessLevel: PropTypes.number,
    placeholderValues: PropTypes.object,
    formId: PropTypes.number,
    componentToEditIndex: PropTypes.number,
    setComponentToEditIndex: PropTypes.func
};

export default FormBuilder;